import { Typography, Paper, Stack } from "@mui/material";
import type { DayHours, TimeRange } from "Types/OpeningHours";
import { createEmptyTimeRange } from "Utils/OpeningHoursUtils";
import { StyledValidationText } from "Components/Shared/FormStyles";
import { Resources, useResource } from "Translations/Resources";
import { DayModeSelector } from "./DayModeSelector";
import type { DayHoursMode } from "./types";
import { TimeRangeList } from "./TimeRangeList";
import { AddTimeRangeButton } from "./AddTimeRangeButton";
import { getDayHoursMode } from "Components/Shared/Inputs/OpeningHoursInput/DayHoursRow/utils";

type Props = {
  dayHours: DayHours;
  onChange: (dayHours: DayHours) => void;
  error?: string;
};

export const DayHoursRow = ({ dayHours, onChange, error }: Props) => {
  const { t } = useResource();

  const { weekday, ranges } = dayHours;

  const handleModeChange = (mode: DayHoursMode) => {
    if (mode === "openAllDay") {
      return onChange({
        weekday: weekday,
        isOpenAllDay: true,
        isClosedAllDay: false,
        ranges: [],
      });
    }

    if (mode === "closedAllDay") {
      return onChange({
        weekday: weekday,
        isOpenAllDay: false,
        isClosedAllDay: true,
        ranges: [],
      });
    }

    return onChange({
      weekday: weekday,
      isOpenAllDay: false,
      isClosedAllDay: false,
      ranges: ranges.length === 0 ? [createEmptyTimeRange()] : ranges,
    });
  };

  const handleAddRange = () => {
    onChange({
      ...dayHours,
      ranges: [...ranges, createEmptyTimeRange()],
    });
  };

  const handleRangeChange = (index: number, newRange: TimeRange) => {
    const newRanges = [...ranges];
    newRanges[index] = newRange;
    onChange({ ...dayHours, ranges: newRanges });
  };

  const handleRangeDelete = (index: number) => {
    const newRanges = ranges.filter((_, i) => i !== index);
    onChange({ ...dayHours, ranges: newRanges });
  };

  const currentMode = getDayHoursMode(dayHours);

  return (
    <Paper sx={{ p: [1.5, 2] }}>
      <Stack gap={2}>
        {weekday && (
          <Typography variant="h6" fontSize={["1rem", "1.25rem"]}>
            {t(Resources.Common.WeekDay[weekday])}
          </Typography>
        )}
        <DayModeSelector value={currentMode} onChange={handleModeChange} />
        {currentMode === "range" && (
          <>
            <TimeRangeList
              ranges={ranges}
              onChange={handleRangeChange}
              onDelete={handleRangeDelete}
            />
            <AddTimeRangeButton onClick={handleAddRange} />
          </>
        )}
        {error && <StyledValidationText>{error}</StyledValidationText>}
      </Stack>
    </Paper>
  );
};
